import { Paper, Text } from '@mantine/core';
import { featureCollection } from '@turf/turf';
import { MapEvent } from 'mapbox-gl';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useResultPage } from '../../hooks/use-result-page';

import { SamplePopup, SamplePopupData, flattenSamples } from '@sampling';

import {
  zoomOnFeature,
  SamplesLayerProps,
  PfMap,
  ZoningLayer,
  SamplesLayers,
} from '@map';

export const ResultMapPage: React.FC = () => {
  const { t } = useTranslation();
  const { result } = useResultPage();
  const [popupData, setPopupData] = useState<SamplePopupData | null>(null);

  const samplesData = result.data?.samples;
  const mapData = result.data?.map;
  const samples = samplesData ? flattenSamples(samplesData) : [];

  const hasMapContent = mapData?.features?.length || samples.length;

  const handleLoad = (map: MapEvent) => {
    if (mapData?.features?.length) {
      zoomOnFeature({
        map: map.target,
        geojson: mapData,
      });
    }
  };

  const handleMarkerClick: SamplesLayerProps['onClick'] = ({
    id: featureId,
    lngLat,
  }) => {
    const sample = samples.find((f) => f.id === featureId);
    const parent = samplesData?.find((s) =>
      s.points.features.some((ss) => ss.id === featureId),
    );

    setPopupData({
      longitude: lngLat.lng,
      latitude: lngLat.lat,
      carbonContent: parent?.carbon_content,
      soilDensity: parent?.soil_density,
      code: sample?.properties?.code || '',
    });
  };

  if (!hasMapContent) {
    return (
      <Paper h="100%" p="lg" withBorder>
        <Text c="dimmed" size="sm">
          {t('sampling.results.map.no_data')}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper h="100%" p={0} style={{ overflow: 'hidden' }}>
      <PfMap onLoad={handleLoad}>
        {mapData ? <ZoningLayer data={mapData} /> : null}
        {samples.length > 0 ? (
          <SamplesLayers
            data={featureCollection(samples)}
            onClick={handleMarkerClick}
            size={30}
            withLabel
          />
        ) : null}
        {popupData && (
          <SamplePopup data={popupData} onClose={() => setPopupData(null)} />
        )}
      </PfMap>
    </Paper>
  );
};
